import { useEffect, useState } from 'react';

export const defaultFonts = ['Roboto', 'Montserrat', 'PT Sans', 'PT Serif'];

// Список Google Fonts большой и нужен при каждой отрисовке бейджа — запрашиваем его один раз
// за сессию. Неудачный ответ не кэшируем, чтобы следующий вызов попробовал ещё раз.
let googleFontsRequest = null;
export const fetchGoogleFonts = () => {
  if (!googleFontsRequest) {
    googleFontsRequest = requestGoogleFonts();
  }
  return googleFontsRequest;
};

const requestGoogleFonts = async () => {
  const apiKey = import.meta.env.VITE_REACT_APP_GF_API_KEY || import.meta.env.REACT_APP_GF_API_KEY;

  if (!apiKey) {
    console.warn('Google Fonts API key not configured (REACT_APP_GF_API_KEY)');
    // Return mock data with default fonts
    return {
      items: defaultFonts.map(font => ({
        family: font,
        files: {
          regular: `https://fonts.googleapis.com/css2?family=${font.replace(' ', '+')}`,
        },
      })),
    };
  }

  try {
    let data = await fetch(`https://www.googleapis.com/webfonts/v1/webfonts?key=${apiKey}`);
    if (!data.ok) {
      throw new Error(`HTTP ${data.status}: ${data.statusText}`);
    }
    data = await data.json();
    return data;
  } catch (error) {
    console.error('Failed to fetch Google Fonts:', error);
    googleFontsRequest = null;
    // Return mock data as fallback
    return {
      items: defaultFonts.map(font => ({
        family: font,
        files: {
          regular: `https://fonts.googleapis.com/css2?family=${font.replace(' ', '+')}`,
        },
      })),
    };
  }
};

// Начертания, которые понимают и браузер, и pdfMake (см. FontMetadata на сервере).
export const FONT_STYLE_NORMAL = 'normal';
export const FONT_STYLE_BOLD = 'bold';
export const FONT_STYLE_ITALIC = 'italic';
export const FONT_STYLE_BOLD_ITALIC = 'bolditalic';

const FONT_FACE_DESCRIPTORS = {
  [FONT_STYLE_NORMAL]: {},
  [FONT_STYLE_BOLD]: { weight: 'bold' },
  [FONT_STYLE_ITALIC]: { style: 'italic' },
  [FONT_STYLE_BOLD_ITALIC]: { weight: 'bold', style: 'italic' },
};

// Ключи начертаний в ответе Google Fonts API (files: { regular, italic, 700, 700italic, ... }).
export const GOOGLE_FILE_STYLES = {
  regular: FONT_STYLE_NORMAL,
  700: FONT_STYLE_BOLD,
  italic: FONT_STYLE_ITALIC,
  '700italic': FONT_STYLE_BOLD_ITALIC,
};

const https = url => (url || '').replace('http://', 'https://');

/** Начертания семейства из ответа Google Fonts API в формате шрифтов аккаунта. */
export const googleFontFaces = item =>
  Object.entries(GOOGLE_FILE_STYLES)
    .filter(([key]) => item.files && item.files[key])
    .map(([key, style]) => ({
      family: item.family,
      style: style,
      source: FONT_SOURCE_GOOGLE,
      url: https(item.files[key]),
    }));

// Промис загрузки по ключу семейство|начертание: повторный вызов ждёт ту же загрузку,
// а не возвращается сразу, пока файл ещё качается.
const LOADED_FONTS = {};
export const loadFontToBrowser = (family, url, style = FONT_STYLE_NORMAL) => {
  if (!family || !url) {
    return Promise.resolve();
  }
  const key = `${family}|${style}`;
  if (!LOADED_FONTS[key]) {
    const font = new FontFace(family, `url(${url})`, FONT_FACE_DESCRIPTORS[style] || {});
    LOADED_FONTS[key] = font
      .load()
      .then(fontFace => {
        document.fonts.add(fontFace);
      })
      .catch(err => {
        // даём следующему вызову шанс перезагрузить шрифт (например после переустановки)
        delete LOADED_FONTS[key];
        console.log(err);
      });
  }
  return LOADED_FONTS[key];
};

const getBase64 = file => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = error => reject(error);
  });
};

export const fetchFontTtf = async url => {
  let ttf = await fetch(url);
  ttf = await ttf.blob();
  ttf = await getBase64(ttf);

  return ttf;
};

// pdfMake ждёт голый base64, без префикса data:<тип>;base64, — а длина префикса зависит
// от content-type (font/ttf, font/otf, application/octet-stream…), резать по фиксированной
// длине нельзя.
export const fetchFontBase64 = async url => {
  const dataUrl = await fetchFontTtf(url);
  return dataUrl.substring(dataUrl.indexOf(',') + 1);
};

/* -----------------------------------------------------------------------------
 * EV-68: шрифты аккаунта.
 *
 * Раньше список добавленных шрифтов лежал в localStorage браузера, поэтому шрифт
 * видел только тот компьютер, на котором его добавили. Теперь список приходит с
 * сервера (GET /api/font) и одинаков для всего аккаунта.
 * -------------------------------------------------------------------------- */

export const FONT_SOURCE_GOOGLE = 'google';
export const FONT_SOURCE_UPLOAD = 'upload';

// Адрес файла шрифта: у google-шрифтов файл лежит у Google, свой — отдаёт наш сервер.
export const fontFileUrl = font => (font.source === FONT_SOURCE_UPLOAD ? `/api/font/${font.fontId}/file` : font.url);

let accountFonts = [];
let loadedForAccountId = null;
const fontListeners = new Set();

export const getAccountFonts = () => accountFonts;

/** Уникальные имена семейств в порядке появления — то, что видно в списке шрифтов. */
export const fontFamilies = (fonts = accountFonts) => [...new Set(fonts.map(font => font.family))];

/** Все начертания семейства: { normal: font, bold: font, ... } */
export const fontFamilyStyles = (family, fonts = accountFonts) =>
  fonts.filter(font => font.family === family).reduce((acc, font) => ({ ...acc, [font.style]: font }), {});

/**
 * Начертания семейства для отрисовки бейджа: { normal: font, bold: font, ... }.
 * Берём из шрифтов аккаунта, недостающие добираем из Google Fonts (дефолтные шрифты и
 * старые, у которых сохранилось только обычное начертание). Превью и PDF должны брать
 * одни и те же файлы: иначе браузер дорисует жирный сам, а в PDF текст выйдет обычным.
 */
export const resolveFontFaces = async family => {
  const styles = fontFamilyStyles(family);
  if (Object.values(GOOGLE_FILE_STYLES).every(style => styles[style])) {
    return styles;
  }
  const googleFonts = await fetchGoogleFonts();
  const item = (googleFonts.items || []).find(googleFont => googleFont.family === family);
  const googleFaces = item ? googleFontFaces(item) : [];
  return googleFaces.reduce((acc, font) => (acc[font.style] ? acc : { ...acc, [font.style]: font }), styles);
};

/** Отдать браузеру все начертания семейств и дождаться, пока они загрузятся. */
export const loadFontsToBrowser = async families => {
  const faces = await Promise.all(families.filter(Boolean).map(resolveFontFaces));
  const loading = faces
    .flatMap(styles => Object.values(styles))
    .map(font => loadFontToBrowser(font.family, fontFileUrl(font), font.style));
  await Promise.all(loading);
};

/**
 * Подставить список шрифтов аккаунта и отдать их браузеру. Возвращает промис, который
 * ждёт загрузки всех файлов, — печать бейджа без этого стартует раньше шрифтов.
 */
export const applyAccountFonts = fonts => {
  accountFonts = fonts || [];
  const loading = accountFonts.map(font => loadFontToBrowser(font.family, fontFileUrl(font), font.style));
  fontListeners.forEach(listener => listener(accountFonts));
  return Promise.all(loading);
};

/** Подписка на список шрифтов аккаунта — обновляется после загрузки/добавления/удаления. */
export const useAccountFonts = () => {
  const [fonts, setFonts] = useState(accountFonts);
  useEffect(() => {
    fontListeners.add(setFonts);
    return () => fontListeners.delete(setFonts);
  }, []);
  return fonts;
};

export const reloadAccountFonts = async fontApi => {
  const { data } = await fontApi.listFonts();
  await applyAccountFonts(data);
  return accountFonts;
};

/**
 * Разовая (на аккаунт) загрузка списка шрифтов: вызывается из App при появлении сессии.
 * Заодно переносит на сервер шрифты, оставшиеся в localStorage от старой версии.
 */
export const loadAccountFontsOnce = (fontApi, accountId) => {
  if (accountId == null || loadedForAccountId === accountId) {
    return;
  }
  loadedForAccountId = accountId;

  // Миграция доступна только админу — на стойке она просто не нужна, поэтому её
  // неудача не должна мешать загрузить сам список.
  migrateLocalStorageFonts(fontApi, accountId)
    .catch(err => console.log(err))
    .then(() => reloadAccountFonts(fontApi))
    .catch(err => {
      loadedForAccountId = null;
      console.log(err);
    });
};

const localStorageKey = accountId => 'fonts' + accountId;

// Старый формат хранения: [{ family, file }] в localStorage. Переносим на сервер и
// подчищаем ключ, чтобы удалённый на сервере шрифт не вернулся при следующем входе.
const migrateLocalStorageFonts = async (fontApi, accountId) => {
  let saved;
  try {
    saved = JSON.parse(window.localStorage.getItem(localStorageKey(accountId)));
  } catch (e) {
    saved = null;
  }
  if (!Array.isArray(saved) || saved.length === 0) {
    window.localStorage.removeItem(localStorageKey(accountId));
    return;
  }

  const fonts = saved
    .filter(font => font && font.family)
    .map(font => ({
      family: font.family,
      style: FONT_STYLE_NORMAL,
      source: FONT_SOURCE_GOOGLE,
      url: (font.file || '').replace('http://', 'https://'),
    }));

  if (fonts.length > 0) {
    await fontApi.addGoogleFonts(fonts);
  }
  window.localStorage.removeItem(localStorageKey(accountId));
};
